/**
 * Enquête n°31 — Pression des pneus moto : comment la contrôler et pourquoi c'est vital.
 * Article premium hebdomadaire.
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const pressionPneusMotoEnquete = {
  slug: 'pression-pneus-moto',
  themeCode: 'M',
  issueNumber: 31,
  totalIssues: 31,
  series: 'enquetes',
  category: 'Enquête mécanique & sécurité',
  shortTitle: 'Pression des pneus',
  title: "Pression pneus moto : 0,3 bar de trop ou de moins, tu changes de moto",
  kicker: 'Enquête n°31 · Série « Témoignages & Sécurité »',
  subtitle:
    "60 % des motards ne vérifient JAMAIS la pression de leurs pneus. Pourtant 0,3 bar d'écart change radicalement le comportement de la moto : trajectoire qui dérive, freinage allongé, usure prématurée, échauffement, voire éclatement. Voici le mode d'emploi complet de la seule vérification mécanique qui peut te sauver la vie.",
  heroImage: 'https://images.unsplash.com/photo-1582340172451-b4ee5b61e974?w=1600&q=80&auto=format&fit=crop',
  heroImageAlt: 'Moto jaune au coucher du soleil — entretien et pression des pneus',
  readTimeMin: 7,
  seoDescription:
    "Enquête sur la pression des pneus moto : valeurs correctes, comment les contrôler, conséquences sous-gonflage / sur-gonflage, signes d'usure. Mode d'emploi complet.",
  seoKeywords:
    'pression pneus moto, sous-gonflage pneu, manomètre moto, témoins d\'usure, TWI, contrôle pneus, pression à froid, pneu éclaté moto',

  edito: {
    title: '50 cm² de gomme, tout repose dessus',
    body: "<strong>Toute la moto, tout ton poids, toutes les forces de freinage, d'accélération et de virage transitent par 50 cm² de gomme</strong>. La taille d'un post-it. Et tu vas continuer à ignorer la pression de tes pneus ? L'<strong>Étude DEKRA 2023</strong> sur 1 200 motos contrôlées révèle : <strong>62 % roulent avec une pression incorrecte</strong> (sur ou sous-gonflage de plus de 0,2 bar). Conséquence directe : <strong>+22 % de distance de freinage à 90 km/h</strong>, perte de contrôle en virage, échauffement excessif. Cette enquête te donne le mode d'emploi.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Où trouver la bonne pression pour TA moto ?',
      paragraphs: [
        "<strong>La pression correcte est indiquée par le constructeur</strong>, pas par le pneu. Tu la trouves à 3 endroits : (1) sur l'étiquette du cadre (souvent près de la béquille latérale ou sous la selle), (2) dans le manuel d'utilisation papier ou PDF, (3) sur la fiche technique en ligne du modèle.",
        "<strong>Exemple type 600 cc routière</strong> : avant 2,3 bar / arrière 2,5 bar (seul) ; avant 2,5 bar / arrière 2,9 bar (en duo). Les valeurs sont TOUJOURS données pour un pneu <strong>FROID</strong> (moto qui n'a pas roulé depuis >2h).",
        "<strong>Erreur classique</strong> : prendre la pression du pneu CHAUD après 30 min de roulage. Le pneu chaud peut afficher +0,3 à +0,5 bar de plus que la valeur à froid. Si tu dégonfles pour atteindre la valeur constructeur, tu te retrouves SOUS-gonflé à froid.",
      ],
      keyPoints: [
        "3 sources : étiquette cadre, manuel, fiche en ligne",
        'Valeurs pour pneu FROID (>2h immobile)',
        "Solo et duo : valeurs différentes",
        'Ne JAMAIS contrôler chaud',
      ],
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 2',
      title: 'Sous-gonflage : le tueur silencieux',
      paragraphs: [
        "<strong>Pneu sous-gonflé de 0,3 bar</strong> : la zone de contact s'élargit, mais devient déformée. La carcasse fléchit excessivement à chaque tour de roue. Conséquences : (1) <strong>échauffement excessif</strong> jusqu'à 90-100 °C en roulant — le caoutchouc se dégrade, perd ses propriétés ; (2) <strong>usure rapide des épaules</strong> (centre du pneu surélevé) ; (3) <strong>trajectoire flottante</strong> en virage — la moto 'cherche', ne tient pas la ligne ; (4) <strong>freinage allongé de 15 à 25 %</strong> (le pneu ne mord plus correctement la route).",
        "<strong>Risque ultime</strong> : <strong>éclatement par échauffement</strong>. Documenté dans plusieurs rapports BEA-TT (Bureau Enquêtes Accidents Transports Terrestres). Un pneu très sous-gonflé qui chauffe + autoroute en plein été = explosion soudaine. Souvent fatal sur 2 roues.",
        "<strong>Signal d'alerte</strong> : si tu sens que ta moto 'flotte' en virage, qu'elle n'est pas franche dans les changements d'angle, ou que tu fais de la consommation supplémentaire — vérifie la pression IMMÉDIATEMENT.",
      ],
      keyPoints: [
        "-0,3 bar = échauffement +25 °C",
        'Freinage allongé 15-25 %',
        'Usure des épaules du pneu',
        'Risque éclatement été/autoroute',
      ],
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 3',
      title: 'Sur-gonflage : le piège du "plus c\'est mieux"',
      paragraphs: [
        "<strong>Pneu sur-gonflé de 0,3 bar</strong> : la surface de contact se réduit. Tu te dis 'tant mieux, ça roule mieux'. Faux. Conséquences : (1) <strong>adhérence réduite</strong> en virage et au freinage — moins de gomme = moins de friction ; (2) <strong>usure rapide du centre du pneu</strong> ; (3) <strong>confort détérioré</strong> (rebonds sur chaque imperfection) ; (4) <strong>tenue de route nerveuse</strong> sur revêtement mouillé ou granuleux.",
        "<strong>Sur-gonflage excessif</strong> (+0,5 bar ou plus) : la chambre à air peut se déformer durablement. Sur autoroute longue distance, des pneus sur-gonflés s'usent en plein centre en quelques milliers de kilomètres seulement.",
        "<strong>Croyance erronée</strong> : 'En duo, je gonfle +0,5 bar pour compenser le poids'. Sauf si le constructeur l'indique précisément, sur-gonfler n'apporte rien — au contraire, ça aggrave le rebond et réduit la zone de contact justement quand tu as besoin de stabilité.",
      ],
      keyPoints: [
        "+0,3 bar = adhérence réduite",
        'Usure du centre du pneu',
        'Confort détérioré',
        'Risque sur revêtement humide',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: "Le test du pneu mort : 7 ans max",
      body: "Un pneu moto a une <strong>date de fabrication</strong> visible sur le flanc : code à 4 chiffres type 2024 (24e semaine de 2024) ou 3724 (37e semaine de 2024). <strong>Au-delà de 5-7 ans, même peu utilisé, le caoutchouc durcit et perd 30 à 50 % de son adhérence</strong>. Surtout sur mouillé. Vérifie : si ton pneu date de 2018, il faut le changer même s'il a 'l'air' neuf. <strong>Le critère TWI</strong> (Tread Wear Indicator) — petits triangles ▲ sur le flanc qui pointent vers des bossages dans les rainures — indique l'usure. Quand la gomme atteint le niveau des bossages, profondeur < 1 mm = pneu mort. La loi exige 1 mm de profondeur résiduelle (R314-1).",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: 'Le protocole de contrôle en 5 minutes',
      paragraphs: [
        "<strong>1. Manomètre fiable</strong> : achète un manomètre digital ou à aiguille de qualité (15-30 € chez Norauto, Dafy). Les compresseurs gratuits des stations-service sont souvent imprécis (+/- 0,2 bar facile). Garde le tien dans le top-case.",
        "<strong>2. Pneu FROID</strong> : contrôle le matin avant de partir, ou après 2h d'immobilisation. Pas après 10 km de roulage.",
        "<strong>3. Dévisse le capuchon de valve</strong> et applique le manomètre franchement et rapidement (sinon tu perds 0,1 bar à chaque essai).",
        "<strong>4. Compare aux valeurs constructeur</strong> (étiquette cadre). Ajuste si écart > 0,1 bar.",
        "<strong>5. Inspection visuelle</strong> : profil régulier (pas d'usure sur un seul côté), pas de coupure, pas d'objet planté, témoins d'usure TWI non atteints, date < 5-7 ans.",
        "<strong>Périodicité</strong> : tous les <strong>15 jours minimum</strong>, et avant chaque trajet > 100 km ou en duo.",
      ],
      keyPoints: [
        'Manomètre personnel obligatoire',
        'Pneu FROID uniquement',
        'Tous les 15 jours minimum',
        'Inspection visuelle systématique',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: "Le piège du 'je vérifie au prochain entretien'",
      body: "Beaucoup de motards attendent la révision annuelle pour faire vérifier la pression. <strong>C'est trop tard</strong> : entre deux révisions, un pneu peut perdre 0,2-0,3 bar par fuite naturelle (perméabilité du caoutchouc, micro-fuites de valve), surtout en hiver (-0,1 bar pour 10 °C de baisse). En 6 mois sans contrôle, tu peux rouler à -0,5 bar sans le savoir. <strong>Le contrôle est ta responsabilité, pas celle du garagiste</strong>. C'est gratuit, c'est rapide, c'est vital.",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'À retenir avant de partir en virée',
      body: "<strong>Tu pars sur l'autoroute pour 300 km en duo avec bagages ?</strong> Vérifie LA VEILLE au soir (pneu froid). Ajuste à la valeur duo+chargement du manuel. Pas après 50 km à l'aire d'autoroute (pneus déjà chauds, valeurs faussées). <strong>Une vérification bimensuelle, c'est 1 minute par roue, 4 fois par mois. 4 min/mois pour rester en vie. Le meilleur ratio coût/sécurité de toute la moto.</strong>",
    },
  ],

  // QCM contextuel : 8 questions du thème « Mécanique » pour valider la compréhension
  quiz: {
    themeCode: 'M',
    questionsCount: 8,
    passingScore: 6,
    title: "QCM : maîtriser la pression et l'entretien des pneus",
    description: '<strong>8 questions</strong> du thème « Mécanique » pour tester ta compréhension. Objectif : <strong>6/8</strong> pour valider.',
  },
};

export default pressionPneusMotoEnquete;
